import { useRef, useEffect, useState } from "react";
import Map from "@arcgis/core/Map";
import MapView from "@arcgis/core/views/MapView";
import Graphic from "@arcgis/core/Graphic";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import Point from "@arcgis/core/geometry/Point";
import Polyline from "@arcgis/core/geometry/Polyline";
import Zoom from "@arcgis/core/widgets/Zoom"; 
import { getBusColor } from "../utils/busColors";
import { useAuth } from "../context/AuthContext";
import { getRoutesByCity, getStopsByRoute } from "../services/mapService";
import { getBusById } from "../services/busService";

// Distancia en metros (Haversine). Se usa para el clamp híbrido del ícono del bus.
const distMeters = (lat1, lon1, lat2, lon2) => {
  const R = 6371e3;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

// Si el GPS crudo cae a más de esto de la línea matcheada, pegamos el ícono a la calle.
const MARKER_SNAP_METERS = 100;

// Dos puntos seguidos de una traza matcheada (OSRM) nunca se separan tanto: por encima de
// esto no es una calle, es un salto de GPS o un fallback recto del matching. Cortamos ahí.
const MAX_SEGMENT_METERS = 1000;

// Limpia la traza que manda el backend y la parte en tramos dibujables.
// Descarta coordenadas inválidas/repetidas y corta la línea donde hay saltos imposibles,
// para no dibujar rectas atravesando media isla cuando el matching falla.
const sanitizePath = (rawPath) => {
  if (!Array.isArray(rawPath)) return { segments: [], head: null };

  const points = [];
  for (const p of rawPath) {
    if (!Array.isArray(p) || p.length < 2) continue;
    const lon = Number(p[0]);
    const lat = Number(p[1]);
    if (!Number.isFinite(lon) || !Number.isFinite(lat)) continue;
    if (lat < -90 || lat > 90 || lon < -180 || lon > 180) continue;
    if (lat === 0 && lon === 0) continue; // fix nulo del GPS

    const prev = points[points.length - 1];
    if (prev && prev[0] === lon && prev[1] === lat) continue; // punto repetido
    points.push([lon, lat]);
  }

  const segments = [];
  let current = [];
  for (const point of points) {
    const prev = current[current.length - 1];
    if (prev && distMeters(prev[1], prev[0], point[1], point[0]) > MAX_SEGMENT_METERS) {
      if (current.length > 1) segments.push(current);
      current = [];
    }
    current.push(point);
  }
  if (current.length > 1) segments.push(current);

  // La cabeza es el último punto válido del payload (aunque su tramo se haya descartado):
  // sirve para pegar el ícono a la calle sin arrastrarlo hacia atrás.
  return { segments, head: points.length > 0 ? points[points.length - 1] : null };
};

export function useBusSystem() {
  const mapRef = useRef(null);
  const viewRef = useRef(null);
  const wsRef = useRef(null);
  const retryTimeoutRef = useRef(null);
  const [clickedBusId, setClickedBusId] = useState(null);
  const busRouteLayers = useRef({});
  const stopsLayer = useRef(null);
  
  // Almacena la historia de segmentos: [ [[x,y],[x,y]], [[x,y],[x,y]] ]
  const busPaths = useRef({}); 
  
  // Bandera para marcar cortes de línea (cuando se oculta un bus)
  const busGapFlags = useRef({});  

  const busCityMap = useRef({});
  const busLastState = useRef({});
  const busPlatesCache = useRef({});
  const hasCentered = useRef(false);
  const stopsCache = useRef({}); 

  // Refs espejo para acceso síncrono en WS
  const citiesRef = useRef({}); 
  const selectedCitiesRef = useRef(new Set());
  const hiddenRoutesRef = useRef(new Set());
  // Lista blanca de rutas que el mapa puede pintar (null = todas). hiddenRoutes solo apaga rutas
  // que el panel lista, pero el websocket manda TODOS los buses, incluso los de rutas ajenas y los
  // que no tienen ruta asignada (rouId null). La fija TrackingPage segun el rol activo.
  const allowedRoutesRef = useRef(null);

  const [activeBuses, setActiveBuses] = useState({});
  const [cities, setCities] = useState({});
  const [selectedCities, setSelectedCities] = useState(new Set());
  const [isConnected, setIsConnected] = useState(false);
  const [routesByCity, setRoutesByCity] = useState({}); 
  const [hiddenRoutes, setHiddenRoutes] = useState(new Set()); 
  const [incidents, setIncidents] = useState([]);
  
  const [busPopup, setBusPopup] = useState(null);
  const [clickedStop, setClickedStop] = useState(null);

  const [permissibleData, setPermissibleData] = useState({});

  const { token } = useAuth(); 
  
  // ==========================================
  // CARGA EN SEGUNDO PLANO DE DATA PERMISIBLE
  // ==========================================
  useEffect(() => {
      const fetchPermissibleData = async () => {
          if (!token || Object.keys(cities).length === 0) return;
          
          const newData = {};
          for (const [citIdStr, cityName] of Object.entries(cities)) {
              try {
                  const citId = Number(citIdStr);
                  const routes = await getRoutesByCity(token, citId);
                  
                  const routesWithStops = await Promise.all(routes.map(async (r) => {
                      const stops = await getStopsByRoute(token, r.rouId);
                      return { ...r, stops: stops || [] };
                  }));
                  newData[citId] = routesWithStops;
              } catch (error) {
                  console.error(`Error cargando permisibles para ${cityName}:`, error);
              }
          }
          setPermissibleData(newData);
      };

      fetchPermissibleData();
  }, [cities, token]);


  // Sincronización Ref-State
  citiesRef.current = cities;

  useEffect(() => {
    const interval = setInterval(() => {
       setActiveBuses({ ...busLastState.current });
    }, 1000); 
    return () => clearInterval(interval);
  }, []);

  const focusOnLocation = (lat, lon) => {
    if (viewRef.current && typeof lat === 'number' && typeof lon === 'number') {
        viewRef.current.goTo({ center: [lon, lat], zoom: 16 }, { duration: 800 })
          .catch(err => { if (err.name !== "AbortError") console.error(err); });
    }
  };

  // ==========================================
  // CONTROLES DE VISIBILIDAD (TOGGLES)
  // ==========================================

  const toggleCity = (citId) => {
    const newSet = new Set(selectedCities);
    const isActivating = !newSet.has(citId);

    if (isActivating) {
      newSet.add(citId);
    } else {
      newSet.delete(citId);
    }

    setSelectedCities(newSet);
    selectedCitiesRef.current = newSet;

    if (isActivating) {
      restoreBusesInCity(citId);
      restoreStopsInCity(citId);
    } else {
      hideBusesInCity(citId);
      hideStopsInCity(citId);
    }
  };

  const toggleRoute = async (rouId) => {
    const newHidden = new Set(hiddenRoutes);
    const isHiding = !newHidden.has(rouId); 

    if (isHiding) {
      newHidden.add(rouId); 
      removeStopsFromMap(rouId); 
      
      Object.keys(busLastState.current).forEach(busId => {
          if (busLastState.current[busId].rouId === rouId) {
              if (busRouteLayers.current[busId]) busRouteLayers.current[busId].removeAll();
              busGapFlags.current[busId] = true; 
          }
      });
    } else {
      newHidden.delete(rouId); 
      await loadAndDrawStops(rouId); 
    }

    setHiddenRoutes(newHidden);
    hiddenRoutesRef.current = newHidden;
    refreshMapLayers();
  };

const toggleRouteGroup = async (rouIdsArray, forceHide = null) => {
    const newHidden = new Set(hiddenRoutesRef.current); 

    // Si le mandamos una orden forzada (true/false), la obedece. Si no, hace el toggle normal.
    const isHiding = forceHide !== null ? forceHide : rouIdsArray.some(id => !newHidden.has(id));

    for (const rouId of rouIdsArray) {
      if (isHiding) {
        newHidden.add(rouId); 
        removeStopsFromMap(rouId); 
        
        Object.keys(busLastState.current).forEach(busId => {
            if (busLastState.current[busId].rouId === rouId) {
                if (busRouteLayers.current[busId]) busRouteLayers.current[busId].removeAll();
                busGapFlags.current[busId] = true;
            }
        });
      } else {
        newHidden.delete(rouId); 
        await loadAndDrawStops(rouId); 
      }
    }

    setHiddenRoutes(newHidden);
    hiddenRoutesRef.current = newHidden;
    refreshMapLayers();
  };

  const isRouteAllowed = (rouId) =>
    allowedRoutesRef.current === null || (rouId != null && allowedRoutesRef.current.has(Number(rouId)));

  // rouIds = rutas permitidas, o null para no filtrar. Quita al instante lo que ya estaba pintado.
  const setAllowedRoutes = (rouIds) => {
    allowedRoutesRef.current = rouIds ? new Set(rouIds.map(Number)) : null;
    if (stopsLayer.current && allowedRoutesRef.current) {
      const graphicsToRemove = stopsLayer.current.graphics.filter(g => g.attributes && !isRouteAllowed(g.attributes.routeId));
      stopsLayer.current.removeMany(graphicsToRemove);
    }
    refreshMapLayers();
  };

  // ==========================================
  // GESTIÓN DE PARADAS
  // ==========================================

  const loadAndDrawStops = async (rouId) => {
    if (!viewRef.current || !stopsLayer.current || !isRouteAllowed(rouId)) return;
    let stops = stopsCache.current[rouId];
    if (!stops) {
      stops = await getStopsByRoute(token,rouId);
      stopsCache.current[rouId] = stops;
    }
    // La lista blanca pudo cambiar mientras llegaban las paradas.
    if (isRouteAllowed(rouId)) drawStopsOnLayer(stops, rouId);
  };

  const drawStopsOnLayer = (stops, rouId) => {
    if (!stops || stops.length === 0) return;
    const graphics = stops.map(stop => {
      const point = new Point({ latitude: stop.stoLatitude, longitude: stop.stoLongitude });
      return new Graphic({
        geometry: point,
        symbol: {
          type: "simple-marker", style: "circle", color: "white", size: "10px",
          outline: { color: "#4B4A4A", width: 2 }
        },
        attributes: { routeId: rouId, stoId: stop.stoId, stopName: stop.stoName },
      });
    });
    stopsLayer.current.addMany(graphics);
  };

  const removeStopsFromMap = (rouId) => {
    if (!stopsLayer.current) return;
    const graphicsToRemove = stopsLayer.current.graphics.filter(g => g.attributes && g.attributes.routeId === rouId);
    stopsLayer.current.removeMany(graphicsToRemove);
  };

  const hideStopsInCity = (citId) => {
    if (routesByCity[citId]) Object.keys(routesByCity[citId]).forEach(rouId => removeStopsFromMap(parseInt(rouId)));
  };

  const restoreStopsInCity = (citId) => {
    if (routesByCity[citId]) {
        Object.keys(routesByCity[citId]).forEach(rouId => {
            const id = parseInt(rouId);
            if (!hiddenRoutesRef.current.has(id)) loadAndDrawStops(id);
        });
    }
  };

  // ==========================================
  // LÓGICA DE BUSES
  // ==========================================

  const hideBusesInCity = (citId) => {
    Object.keys(busCityMap.current).forEach((busId) => {
      if (busCityMap.current[busId] === citId) {
        const layer = busRouteLayers.current[busId];
        if (layer) layer.removeAll();
        busGapFlags.current[busId] = true;
      }
    });
  };

  const restoreBusesInCity = (citId) => {
    Object.keys(busLastState.current).forEach((busId) => {
      const state = busLastState.current[busId];
      if (state && state.citId === citId) drawBusOnMap(state);
    });
  };

  const refreshMapLayers = () => {
    Object.keys(busLastState.current).forEach(busId => {
      const data = busLastState.current[busId];
      if (data) drawBusOnMap(data);
    });
  };

  const drawBusOnMap = (data) => {
    if (!viewRef.current || !viewRef.current.map) return;
    const { busId, path, lastLat, lastLon, citId, rouId, type } = data;
    
    if (typeof lastLat !== 'number' || typeof lastLon !== 'number') return;

    if (!selectedCitiesRef.current.has(citId) || hiddenRoutesRef.current.has(rouId) || !isRouteAllowed(rouId)) {
      if (busRouteLayers.current[busId]) {
          busRouteLayers.current[busId].removeAll();
          busGapFlags.current[busId] = true;
      }
      return; 
    }

    const map = viewRef.current.map;

    if (!busRouteLayers.current[busId]) {
      const layer = new GraphicsLayer();
      busRouteLayers.current[busId] = layer;
      map.add(layer);
      // 🔁 una SOLA traza por bus (se reemplaza, no se acumula)
      busPaths.current[busId] = { segments: [], head: null };
    }

    const layer = busRouteLayers.current[busId];
    layer.removeAll();

    const busColor = getBusColor(busId);

    try {
        // 🔁 MODELO REEMPLAZO: salvo 'cursor', la línea del bus ES EXACTAMENTE la última traza
        // que mandó el backend (ya re-matcheada y limpia). No se acumula nada encima: si el
        // matching se corrigió, esta línea nueva sustituye a la anterior.
        if (type !== "cursor" && Array.isArray(path)) {
            const cleaned = sanitizePath(path);
            const previous = busPaths.current[busId];
            busPaths.current[busId] = {
                // Si la traza nueva quedó sin tramos válidos (matching caído, solo dos puntos
                // lejanos), conservamos la última buena en vez de sustituirla por una recta.
                segments: cleaned.segments.length > 0 ? cleaned.segments : (previous?.segments || []),
                head: cleaned.head || previous?.head || null
            };
        }

        const trail = busPaths.current[busId] || { segments: [], head: null };

        // Línea del bus: cada tramo va aparte, así el corte por salto no se dibuja.
        if (trail.segments.length > 0) {
            const polyline = new Polyline({
                paths: trail.segments,
                spatialReference: { wkid: 4326 }
            });
            const routeGraphic = new Graphic({
                geometry: polyline,
                symbol: {
                    type: "simple-line", color: busColor, width: 4,
                    join: "round", cap: "round"
                },
            });
            layer.add(routeGraphic);
        }

        // 🧭 ÍCONO HÍBRIDO: por defecto la posición cruda del GPS (movimiento suave y en vivo).
        // Pero si esa posición cae MUY lejos de la línea ya matcheada (mal fix o fuera de calle),
        // pegamos el ícono a la cabeza de la línea para que no se vea "flotando" sobre terreno vacío.
        let markerLon = lastLon;
        let markerLat = lastLat;
        const head = trail.head;
        if (head && distMeters(lastLat, lastLon, head[1], head[0]) > MARKER_SNAP_METERS) {
            markerLon = head[0];
            markerLat = head[1];
        }

        const busGraphic = new Graphic({
          geometry: new Point({
            latitude: markerLat, longitude: markerLon, spatialReference: { wkid: 4326 }
          }),
          symbol: {
            type: "simple-marker", color: busColor, size: "14px",
            outline: { color: "white", width: 2 },
          },
          attributes: { type: "bus", busId, busRegistrationId: data.busRegistrationId, routeName: data.rouName || "Desconocida", busColor }
        });

        // El marcador siempre encima de la línea.
        layer.add(busGraphic);

    } catch (error) {
        console.error(`Error dibujando línea Bus ${busId}:`, error);
    }
  };

  // ==========================================
  // INICIALIZACIÓN Y WEBSOCKET
  // ==========================================
  
  useEffect(() => {
    let view; 
    const map = new Map({ basemap: "streets-navigation-vector" });
    
    view = new MapView({
      container: mapRef.current,
      map,
      center: [-66.820, 10.410],
      zoom: 13,
      ui: { components: [] }, 
    });
    viewRef.current = view;

    const sLayer = new GraphicsLayer({ title: "StopsLayer" });
    map.add(sLayer);
    stopsLayer.current = sLayer;

    view.when(() => {
      const zoom = new Zoom({ view: view });
      view.ui.add(zoom, "top-left");
      const style = document.createElement("style");
      style.innerHTML = `.esri-view-surface--inset-outline:focus::after { display: none !important; } .esri-view:focus { outline: none !important; }`;
      document.head.appendChild(style);
      view.container.addEventListener("focus", (e) => e.target.blur(), true);

      // 🔥 LÓGICA DE DETECCIÓN DEL HOVER (POINTER-MOVE)
      view.on("pointer-move", async (event) => {
        const response = await view.hitTest(event);
        
        // Buscamos si el ratón está tocando un Bus o una Parada
        const hitResult = response.results.find(res => 
            res.graphic?.attributes?.type === "bus" || res.graphic?.attributes?.stopName
        );

        if (hitResult) {
          const attr = hitResult.graphic.attributes;
          
          if (attr.type === "bus") {
              const plate = attr.busRegistrationId || busPlatesCache.current[attr.busId];
              setBusPopup({
                 x: event.x,
                 y: event.y - 15,
                 title: plate ? `BUS ${plate}` : `BUS #${attr.busId}`,
                 subtitle: attr.routeName,
                 color: attr.busColor
              });
          } else if (attr.stopName) {
              setBusPopup({
                 x: event.x,
                 y: event.y - 15,
                 title: `PARADA`,
                 subtitle: attr.stopName,
                 color: "#4B4A4A" 
              });
          }
          document.body.style.cursor = "pointer";
        } else {
          setBusPopup((prev) => (prev ? null : prev)); 
          document.body.style.cursor = "default";
        }
      });

      // 1. LÓGICA DE CLIC EN EL MAPA
view.on("immediate-click", async (event) => {
        const response = await view.hitTest(event);
        
        // Detectamos si tocó un bus o una parada
        const busHit = response.results.find(res => res.graphic?.attributes?.type === "bus");
        const stopHit = response.results.find(res => res.graphic?.attributes?.type === "permissible-stop" || res.graphic?.attributes?.stopName);

        // 🔥 LÓGICA DE ZOOM INTELIGENTE
        // Si el zoom actual es menor a 15, acércate a 15. Si es mayor o igual, mantén el actual.
        const targetZoom = view.zoom < 15 ? 15 : view.zoom;

        if (busHit) {
          const attr = busHit.graphic.attributes;
          view.goTo({ target: busHit.graphic.geometry, zoom: targetZoom }, { duration: 500 });
          setClickedBusId(attr.busId); 
          setClickedStop(null); // Oculta la parada si toca un bus
        } else if (stopHit) {
          // 🔥 SI TOCA UNA PARADA, GUARDA LA DATA
          const attr = stopHit.graphic.attributes;
          view.goTo({ target: stopHit.graphic.geometry, zoom: targetZoom }, { duration: 500 });
          setClickedStop({
              name: attr.name || attr.stopName,
              stoId: attr.stoId || attr.stopId,
              rouId: attr.rouId,
              citId: attr.citId
          });
          setClickedBusId(null);
        } else {
          setBusPopup(null);
          setClickedStop(null);
        }
      });

      view.on("pointer-leave", () => {
         setBusPopup(null);
         document.body.style.cursor = "default";
      });
    });

    const connectWs = () => {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) return;
      if (!token) return;

      const baseUrl = "wss://ws-prd.enlaparadave.com/employee-native-websocket/map";
      const wsUrl = `${baseUrl}?token=${token}`;
      
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        setIsConnected(true);
        if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          if (data.type === "INCIDENT" || data.type === "INCIDENT_DETECTED") {
            setIncidents(prev => {
                const filteredIncidents = prev.filter(inc => inc.rouId !== data.rouId);
                return [data, ...filteredIncidents];
            }); 
            return; 
          } else if (data.type === "CLEAR_INCIDENTS") {
            setIncidents([]); 
            return;
          }
          else if (data.type === "REMOVE_INCIDENT") {
            setIncidents(prev => prev.filter(inc => inc.rouId !== data.rouId));
            return;
          }

          const { busId, citId, citName, lastLat, lastLon, rouId, rouName } = data;
          if (!busId || !citId) return;

          if (!citiesRef.current[citId]) {
              const updatedSelected = new Set(selectedCitiesRef.current).add(citId);
              selectedCitiesRef.current = updatedSelected;

              setSelectedCities(updatedSelected);
              setCities(prev => ({ ...prev, [citId]: citName }));
          }

          if (rouId && rouName) {
            setRoutesByCity(prev => {
                const cityRoutes = prev[citId] || {};
                if (!cityRoutes[rouId]) {
                  const newState = { ...prev, [citId]: { ...cityRoutes, [rouId]: rouName } };
                  if (!hiddenRoutesRef.current.has(rouId)) loadAndDrawStops(rouId);
                  return newState;
                }
                return prev;
            });
          }

          busCityMap.current[busId] = citId;

          if (!data.busRegistrationId && busPlatesCache.current[busId]) {
            data.busRegistrationId = busPlatesCache.current[busId];
          }

          busLastState.current[busId] = data;

          if (!data.busRegistrationId && !(busId in busPlatesCache.current)) {
            busPlatesCache.current[busId] = null;
            getBusById(busId, token).then((bus) => {
              if (bus && bus.busRegistrationId) {
                busPlatesCache.current[busId] = bus.busRegistrationId;
                if (busLastState.current[busId]) {
                  busLastState.current[busId].busRegistrationId = bus.busRegistrationId;
                }
              }
            }).catch(() => {});
          }

          drawBusOnMap(data);

          // Solo se centra en un bus que este usuario puede ver.
          if (!hasCentered.current && typeof lastLat === "number" && isRouteAllowed(rouId)) {
            view.when(() => {
              view.goTo({ center: [lastLon, lastLat], zoom: 15 }).catch((e) => {
                if (e.name !== "AbortError") console.warn(e);
              });
            });
            hasCentered.current = true;
          }
        } catch (e) {
          console.error("Error WS:", e);
        }
      };

      ws.onclose = (e) => {
        setIsConnected(false);
        wsRef.current = null;
        if (token) {
            if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
            retryTimeoutRef.current = setTimeout(() => connectWs(), 3000); 
        }
      };
      
      ws.onerror = () => ws.close();
    };

    if (token) connectWs();

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !isConnected && token) connectWs();
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
      if (wsRef.current) {
        wsRef.current.onclose = null; 
        wsRef.current.close();
      }
      if (view) view.destroy();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]); 

  return { 
      mapRef, cities, selectedCities, isConnected, toggleCity, 
      routesByCity, hiddenRoutes, toggleRoute, toggleRouteGroup, setAllowedRoutes, incidents,
      activeBuses, focusOnLocation,
      busPopup, clickedBusId, setClickedBusId,
      permissibleData,clickedStop,setClickedStop // 🔥 Exportamos la data pre-cargada para TrackingPage
  };
}